import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Dog, Heart, Calendar, Users, Camera, MapPin, Trophy, Sparkles } from 'lucide-react';

// Images
const TOBY_IMAGE = "https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/92iyvmuc_Tobi%20CBO%20Puppoto.jpg";
const PAUL_IMAGE = "https://customer-assets.emergentagent.com/job_tradie-leads-1/artifacts/34zna65a_Paul%20Klerck.jpg";

const PuppotoComingSoon = () => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [showAdminLogin, setShowAdminLogin] = useState(false);
  const [adminPassword, setAdminPassword] = useState('');
  const [adminError, setAdminError] = useState('');

  // May 1st 2026 Midnight AEST
  const launchDate = new Date('2026-05-01T00:00:00+10:00').getTime();

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const difference = launchDate - now;

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      } else {
        // Launch time reached - redirect to main app
        window.location.href = '/puppoto';
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launchDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (email) {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        await fetch(`${API_URL}/api/puppoto/waitlist`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email })
        });
        setSubmitted(true);
      } catch (err) {
        console.error('Waitlist error:', err);
        setSubmitted(true); // Show success anyway for UX
      }
    }
  };

  const handleAdminLogin = (e) => {
    e.preventDefault();
    if (adminPassword === 'puppylove!') {
      // Store admin access in session
      sessionStorage.setItem('puppoto_admin_access', 'true');
      window.location.href = '/puppoto';
    } else {
      setAdminError('Incorrect password');
    }
  };

  const features = [
    { icon: Heart, text: "Tinder-style swiping for breeding & playdates" },
    { icon: Camera, text: "Instagram-style photo feeds for all dogs" },
    { icon: Users, text: "Browse rescue dogs & adopt" },
    { icon: MapPin, text: "Find doggy day care & boarding" },
    { icon: Trophy, text: "Local dog shows & events" },
    { icon: Sparkles, text: "Memorial wall for beloved fur babies" },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 overflow-hidden">
      <Helmet>
        <title>Puppoto - Coming April 1st 2026 | The Complete Lifecycle Platform for Dog Lovers</title>
        <meta name="description" content="Puppoto is coming! Join the global pack of dog lovers. Tinder-style swiping for playdates, Instagram-style feeds, find groomers, vets, trainers and more. Launching April 1st 2026." />
        <link rel="canonical" href="https://puppoto.com" />
      </Helmet>
      {/* Animated paw prints background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none opacity-10">
        {[...Array(20)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute text-6xl"
            initial={{ opacity: 0, y: -100 }}
            animate={{ 
              opacity: [0, 1, 0],
              y: ['0vh', '120vh'],
              x: Math.random() * 100 + 'vw'
            }}
            transition={{
              duration: 10 + Math.random() * 10,
              repeat: Infinity,
              delay: Math.random() * 5
            }}
          >
            🐾
          </motion.div>
        ))}
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 md:py-16">
        {/* Logo */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <Dog className="w-12 h-12 md:w-16 md:h-16 text-orange-500" />
            <h1 className="text-5xl md:text-7xl font-black bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-500 bg-clip-text text-transparent">
              Puppoto
            </h1>
          </div>
          <p className="text-lg md:text-xl text-gray-600 font-medium">
            The Complete Lifecycle Platform for Dog Lovers
          </p>
        </motion.div>

        {/* Main Content */}
        <div className="max-w-5xl mx-auto">
          {/* Countdown Section */}
          <motion.div 
            className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-6 md:p-10 mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-center text-gray-800 mb-2">
              🐾 No Jokes Here—Just More Wagging Tails! 🐾
            </h2>
            <p className="text-center text-orange-600 font-semibold text-lg mb-6">
              On May 1st, Puppoto officially arrives, and we aren't kidding!
            </p>

            {/* Countdown Timer */}
            <div className="grid grid-cols-4 gap-2 md:gap-4 max-w-lg mx-auto mb-8">
              {[
                { value: timeLeft.days, label: 'Days' },
                { value: timeLeft.hours, label: 'Hours' },
                { value: timeLeft.minutes, label: 'Minutes' },
                { value: timeLeft.seconds, label: 'Seconds' }
              ].map((item, i) => (
                <motion.div 
                  key={item.label}
                  className="bg-gradient-to-br from-orange-400 to-amber-500 rounded-2xl p-3 md:p-4 text-center shadow-lg"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 + i * 0.1 }}
                >
                  <div className="text-3xl md:text-5xl font-black text-white">
                    {String(item.value).padStart(2, '0')}
                  </div>
                  <div className="text-xs md:text-sm text-white/90 font-medium uppercase tracking-wide">
                    {item.label}
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Statement */}
            <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-2xl p-6 mb-8 border border-orange-200">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed text-center mb-6">
                We're going global to bring dog lovers together like never before. Whether you want to share a "Gotcha Day" post, 
                find a local playdate, or browse for your next four-legged family member, <strong>Puppoto is your new digital home.</strong>
              </p>
              
              <h3 className="text-xl font-bold text-gray-800 text-center mb-4">What's waiting for you inside:</h3>
              
              <div className="grid md:grid-cols-2 gap-4 text-left max-w-2xl mx-auto">
                <div className="bg-white/70 rounded-xl p-4 border border-orange-100">
                  <h4 className="font-bold text-orange-600 mb-1">🎉 Social & Fun</h4>
                  <p className="text-gray-600 text-sm">Instagram-style feeds and "Tinder-style" swiping for playdates and breeding.</p>
                </div>
                <div className="bg-white/70 rounded-xl p-4 border border-orange-100">
                  <h4 className="font-bold text-orange-600 mb-1">💝 Care & Support</h4>
                  <p className="text-gray-600 text-sm">Find top-rated groomers, vets, trainers, and pet sitters near you.</p>
                </div>
                <div className="bg-white/70 rounded-xl p-4 border border-orange-100">
                  <h4 className="font-bold text-orange-600 mb-1">🛍️ Shop & Learn</h4>
                  <p className="text-gray-600 text-sm">Access the best nutrition brands, insurers, and local pet shops.</p>
                </div>
                <div className="bg-white/70 rounded-xl p-4 border border-orange-100">
                  <h4 className="font-bold text-orange-600 mb-1">🌈 Heart & Soul</h4>
                  <p className="text-gray-600 text-sm">A dedicated memorial wall to honor our beloved fur babies.</p>
                </div>
              </div>
              
              <p className="text-center text-gray-700 font-semibold mt-6">
                Stop scrolling and start sniffing around! <span className="text-orange-600">Join the global pack this April.</span>
              </p>
            </div>

            {/* Waitlist Form */}
            {!submitted ? (
              <form onSubmit={handleSubmit} className="max-w-md mx-auto">
                <p className="text-center text-gray-600 mb-4">
                  Join the pack! Get notified when we launch 🐾
                </p>
                <div className="flex gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="flex-1 px-4 py-3 rounded-xl border border-orange-200 focus:outline-none focus:ring-2 focus:ring-orange-400"
                    required
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-gradient-to-r from-orange-500 to-amber-500 text-white font-bold rounded-xl hover:shadow-lg transition-all"
                  >
                    Join! 🐕
                  </button>
                </div>
              </form>
            ) : (
              <motion.div 
                className="text-center text-green-600 font-semibold text-lg"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
              >
                🎉 Woof! You're on the list! We'll bark at you when we launch!
              </motion.div>
            )}
          </motion.div>

          {/* Leadership Section */}
          <motion.div 
            className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl p-6 md:p-10"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3 className="text-2xl font-bold text-center text-gray-800 mb-8">
              Meet the Pack Leaders
            </h3>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-2xl mx-auto">
              {/* Toby - CBO */}
              <div className="text-center">
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 rounded-full overflow-hidden border-4 border-orange-400 shadow-xl">
                  <img 
                    src={TOBY_IMAGE}
                    alt="Toby - Chief Barking Officer"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-bold text-gray-800">Toby</h4>
                <p className="text-orange-600 font-semibold">Chief Barking Officer (CBO)</p>
                <p className="text-gray-500 text-sm mt-1">Pomchi • Official Mascot</p>
              </div>

              {/* Paul - Alpha Pack Leader */}
              <div className="text-center">
                <div className="w-32 h-32 md:w-40 md:h-40 mx-auto mb-4 rounded-full overflow-hidden border-4 border-orange-400 shadow-xl">
                  <img 
                    src={PAUL_IMAGE}
                    alt="Paul Klerck - Alpha Pack Leader"
                    className="w-full h-full object-cover"
                  />
                </div>
                <h4 className="text-xl font-bold text-gray-800">Paul Klerck</h4>
                <p className="text-orange-600 font-semibold">Alpha Pack Leader</p>
                <p className="text-gray-500 text-sm mt-1">Founder & CEO</p>
              </div>
            </div>
          </motion.div>

          {/* Admin Access Link */}
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAdminLogin(!showAdminLogin)}
              className="text-gray-400 text-sm hover:text-gray-600 transition-colors"
            >
              Team Access
            </button>
            
            {showAdminLogin && (
              <motion.form 
                onSubmit={handleAdminLogin}
                className="mt-4 max-w-xs mx-auto"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <input
                  type="password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  placeholder="Admin password"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-400 mb-2"
                />
                {adminError && <p className="text-red-500 text-sm mb-2">{adminError}</p>}
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Access Full Site
                </button>
              </motion.form>
            )}
          </div>

          {/* Footer */}
          <div className="text-center mt-12 text-gray-500 text-sm">
            <p>🇦🇺 Made with ❤️ in Australia</p>
            <p className="mt-1">A Lionheart Digital Production</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PuppotoComingSoon;
